import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { shapes, Filter } from '@unite-us/client-utils';
import { Spinner } from 'common/spinners';
import { DynamicTable } from 'common/tables';
import { Drawer } from '@unite-us/ui';
import { Popover } from '@unite-us/app-components';
import CaseRowIcon from 'src/components/Dashboard/components/Cases/CaseRowIcon';
import { TOP_NAVIGATION_HEIGHT } from 'src/components/Navigation/constants';
import _ from 'lodash';
import firstLine from 'common/utils/firstLine';
import { showFacesheetCasesTableDescription } from 'common/utils/FeatureFlags/flags';
import FilterBar from '../FilterBar';
import { setFilterSearchPlaceholder, setAsyncSearch } from '../../utils/filter';
import './AllCasesIndex.scss';
import {
  clientNameColumn,
  serviceTypeColumn,
} from '../../utils/columnConfigs/baseColumns';

export function AllCasesIndex(props) {
  const {
    paging,
    data,
    onNextClick,
    onPrevClick,
    isFetching,
    onCellClick,
    initialSort,
    isCC,
    filters,
    onFiltersChange,
    isCaseMgmtProvider,
    isCaseManager,
    fetchGroupsUsers,
    onClose,
    onToggle,
    open,
    showDescription,
    sortConfig,
    setSort,
  } = props;
  const secondaryFilters = filters.filter((f) => _.get(f, 'secondary'));

  const [pendingAuthPopoverCaseId, setPendingAuthPopoverCaseId] = useState(null);

  const onSortColumn = (column, sortDirection) => {
    if (sortConfig) {
      sortConfig(column, sortDirection);
    }
  };

  function getColumns() {
    const descColumn = {
      label: 'Description',
      formatData: (item) => firstLine(item.description),
      className: 'truncate',
    };

    return _.compact([
      clientNameColumn(setSort, onSortColumn),
      showDescription ? descColumn : null,
      serviceTypeColumn(),
      {
        label: 'Status',
        sortColumn: 'status',
        formatData: (item) => (_.isEmpty(item.outcome) ? 'Open' : 'Closed'),
        type: 'status',
      },
      {
        label: 'Date Opened',
        sortColumn: 'opened_date',
        sortBy: setSort?.sidx,
        sortDirection: setSort?.sord,
        setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
        type: 'date',
      },
      {
        label: 'Last Updated',
        sortColumn: 'updated_at',
        sortBy: setSort?.sidx,
        sortDirection: setSort?.sord,
        setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
        type: 'date',
      },
    ]);
  }

  const isCasePendingAuth = (serviceCase) => (
    isCaseManager && _.get(serviceCase, 'state') === 'pending_authorization'
  );

  const togglePendingAuthPopover = (serviceCase) => {
    if (!isCasePendingAuth(serviceCase)) {
      setPendingAuthPopoverCaseId(null);
      return false;
    }

    const caseId = serviceCase?.id;
    if (!caseId) {
      setPendingAuthPopoverCaseId(null);
      return true;
    }

    setPendingAuthPopoverCaseId((current) => (
      current === caseId ? null : caseId
    ));
    return true;
  };

  const handleCellClick = (row, column) => {
    const serviceCase = data?.[row];
    if (!serviceCase) {
      return;
    }
    if (togglePendingAuthPopover(serviceCase)) {
      return;
    }
    onCellClick(row, column);
  };

  return (
    <div>
      <h1 className="sr-only" id="dashboard-main-content-heading">Internal Cases - All</h1>
      <FilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        paging={paging}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        isFetching={isFetching && !_.isEmpty(data)}
        asyncSearch={fetchGroupsUsers}
        onMoreFiltersClick={onToggle}
        showSecondaryFilters
      />
      <div className="dashboard-inner-content">
        {
          (isFetching && _.isEmpty(data)) ?
            <Spinner /> : (
              <DynamicTable
                caption="All cases"
                id="all-cases-table"
                dataSet={data}
                onCellClick={handleCellClick}
                initialSort={initialSort}
                columnLabels={getColumns()}
                getRowProps={(serviceCase) => (
                  isCasePendingAuth(serviceCase) ? {
                    clickable: false,
                    hoverable: false,
                  } : null
                )}
                rowIcon={(serviceCase) => {
                  const icon = (
                    <CaseRowIcon
                      isCC={isCC}
                      isCaseMgmtProvider={isCaseMgmtProvider}
                      serviceCase={serviceCase}
                    />
                  );

                  if (!isCasePendingAuth(serviceCase)) {
                    return icon;
                  }

                  return (
                    <Popover
                      id={`pending-auth-all-cases-${serviceCase.id}`}
                      content="You can't access cases that are pending authorization. Try again later."
                      simpleMode
                      isVisible={pendingAuthPopoverCaseId === serviceCase.id}
                    >
                      {icon}
                    </Popover>
                  );
                }}
                emptyMessage="There are no cases."
              />
            )
        }
      </div>
      <Drawer
        className="all-cases__drawer"
        open={open}
        secondary
        onClose={onClose}
        topOffset={TOP_NAVIGATION_HEIGHT}
      >
        <div className="all-cases__drawer-contents">
          <div className="all-cases__drawer-header">
            <h2 className="all-cases__drawer-header-label">
              More Filter(s)
            </h2>
          </div>
          <div className="all-cases__drawer-body">
            {
                _.map(_.orderBy(secondaryFilters, ['name'], ['desc']), (f) => {
                  const {
                    asyncSearch: filterAsyncSearch,
                    searchPlaceholder,
                  } = f;

                  if (!f.secondary) {
                    return null;
                  }

                  return (
                    <Filter
                      hideSelectAll={f.hideSelectAll || false}
                      key={f.key}
                      name={f.name}
                      pluralName={f.pluralName}
                      options={f.options}
                      onFiltersChange={(activeFilters) => onFiltersChange(f.key, activeFilters)}
                      searchPlaceholder={searchPlaceholder || setFilterSearchPlaceholder(f)}
                      asyncSearch={filterAsyncSearch !== undefined ?
                        setAsyncSearch(f, filterAsyncSearch) :
                        setAsyncSearch(f, fetchGroupsUsers)}
                      uniqIdPrefix={f.key}
                    />
                  );
                })
              }
          </div>
        </div>
      </Drawer>
    </div>
  );
}

AllCasesIndex.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  paging: shapes.paging,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  onCellClick: PropTypes.func.isRequired,
  initialSort: PropTypes.object,
  isCC: PropTypes.bool.isRequired,
  filters: PropTypes.array,
  onFiltersChange: PropTypes.func,
  isCaseMgmtProvider: PropTypes.bool,
  fetchGroupsUsers: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  showDescription: PropTypes.bool,
  sortConfig: PropTypes.func,
  setSort: PropTypes.object,
  isCaseManager: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    showDescription: showFacesheetCasesTableDescription(state),
  };
}

export default connect(mapStateToProps)(AllCasesIndex);
